import { useMutation, useQuery } from '@apollo/client'
import { useCallback, useMemo, useState } from 'react'
import NumberFormat from 'react-number-format'
import { useHistory, useParams } from 'react-router'

import { Button, Card, CryptoIcon, Input, Spinner } from '../../components'
import { EtherscanAddress } from '../../components/etherscan-address'
import { LoanQualification } from '../../components/loan-qualification'
import { PoolsTable } from '../../components/pools-table'
import { Paths } from '../../config/routes'
import { Notify } from '../../lib/helpers'
import { GetLoan } from '../loan/queries'
import { GetPreQualificationData, UpdateLoanApplication } from './queries'

// The node API its requiring a collateral whenver an update its made so for now we have to live with this
const mockCollateral = {
    asset: 'a',
    asset_type: 'a',
    floor_value: 10,
    iso_currency_code: 'USD',
    owner_account: 'a',
    value: 10,
    metadata: {
        chain: 'a',
        contract_address: 'a',
        metadata_url: 'a',
        token_id: 'a',
        token_standard: 'a',
    },
}

export function ReviewLoanApplication() {
    let { id } = useParams<{ id: string }>()
    const history = useHistory()

    const [updateLoanApplication] = useMutation(UpdateLoanApplication)

    const { data, loading } = useQuery(GetLoan, {
        variables: { id },
    })
    const loan = useMemo(() => {
        return loading ? null : data.getOneLoanApplicationFromNode.data
    }, [data, loading])

    const {
        data: preQualificationRequest,
        loading: loadingPreQualificationData,
    } = useQuery(GetPreQualificationData, {
        variables:
            loan !== null
                ? {
                      // This is just a placehodler we probably need a field to store the id of the user that generated the application
                      userId: loan.name,
                  }
                : undefined,
        fetchPolicy: 'network-only',
    })

    const handleApprove = useCallback(async () => {
        const loan_application = {
            ...loan,
            status: '1',
            data: {
                ...loan.data,
                collateral: mockCollateral,
                __typename: undefined,
            },
            __typename: undefined,
        }
        await updateLoanApplication({
            variables: {
                loan_application,
            },
        })
        Notify('success', 'Yippee!', 'Loan Approved')
        history.push(Paths.LOAN_APPLICATIONS)
    }, [updateLoanApplication, loan, history])

    const handleReject = useCallback(async () => {
        const loan_application = {
            ...loan,
            status: '2',
            data: {
                ...loan.data,
                collateral: mockCollateral,
                __typename: undefined,
            },
            __typename: undefined,
        }
        await updateLoanApplication({
            variables: {
                loan_application,
            },
        })
        Notify(
            'success',
            'Loan reviewed',
            'You have declined loan ' + loan.loan_application_id,
        )
        history.push(Paths.LOAN_APPLICATIONS)
    }, [updateLoanApplication, loan, history])

    const [loanAmount, setLoanAmount] = useState('')

    // This will trigger a re render that could generate problems
    if (loanAmount === '' && loan?.data.amount > 0) {
        setLoanAmount(loan?.data.amount)
    }

    const dataClasses = 'not-italic font-light text-sm leading-3 py-4'
    const enfasisClasses = 'text-black'

    return (
        <>
            <div className='grid grid-cols-6 gap-16 pt-8'>
                <div className='col-span-6 xl:col-span-2'>
                    <Card className='w-full h-full py-8'>
                        {loadingPreQualificationData ? (
                            <div className='w-full h-full flex justiyf-center items-center'>
                                {' '}
                                <Spinner color='black' size='large' />{' '}
                            </div>
                        ) : (
                            <LoanQualification
                                review
                                value={loan?.amount ?? 0}
                                debtToIncomeRatio={
                                    preQualificationRequest
                                        ?.getPreQualificationData
                                        .debtToIncomeRatio ?? 0
                                }
                                freeCashFlow={
                                    preQualificationRequest
                                        ?.getPreQualificationData
                                        .freeCashFlow ?? 0
                                }
                                monthlyIncome={
                                    preQualificationRequest
                                        ?.getPreQualificationData
                                        .monthlyIncome ?? 0
                                }
                                totalCryptoWorth={
                                    preQualificationRequest
                                        ?.getPreQualificationData
                                        .totalCryptoWorth ?? 0
                                }
                                totalDebt={
                                    preQualificationRequest
                                        ?.getPreQualificationData.totalDebt ?? 0
                                }
                                totalNetWorth={
                                    preQualificationRequest
                                        ?.getPreQualificationData
                                        .totalNetWorth ?? 0
                                }
                                totalAssets={
                                    preQualificationRequest
                                        ?.getPreQualificationData.totalAssets ??
                                    0
                                }
                            />
                        )}
                    </Card>
                </div>
                <div className='col-span-6 xl:col-span-4'>
                    <Card className=' w-full  h-full py-8'>
                        <h3>🌽 Masa USDC A Pool</h3>
                        <PoolsTable
                            headers={[
                                'Asset',
                                'Masa NFT USDC',
                                'Pool Balance',
                                'Pool Utilization',
                            ]}
                            items={
                                <>
                                    <tr
                                        className='border-b border-gray border-solid border-opacity-10 group'
                                        data-test-id={`transaction-item`}>
                                        <td
                                            className={`${dataClasses} ${enfasisClasses}`}>
                                            <div className='flex flex-row justify-center w-full p-2'>
                                                <CryptoIcon name='USDC' />
                                            </div>
                                        </td>
                                        <td
                                            className={`${dataClasses} ${enfasisClasses}`}>
                                            <div className='flex truncate flex-row justify-center'>
                                                <EtherscanAddress
                                                    address={'0x7899...67h7'}
                                                />
                                            </div>
                                        </td>
                                        <td
                                            className={`${dataClasses} ${enfasisClasses}`}>
                                            <div className='flex break-words overflow-visible truncate text-left flex-col items-center'>
                                                <p className='text-black font-normal'>
                                                    $134,608.00
                                                </p>
                                            </div>
                                        </td>
                                        <td
                                            className={`${dataClasses} ${enfasisClasses}`}>
                                            <div className='flex break-words overflow-visible truncate text-left flex-col items-center'>
                                                <p>
                                                    <span className='text-black font-normal'>
                                                        $50,000.00
                                                    </span>{' '}
                                                    borrowed
                                                </p>
                                                <p>
                                                    <span className='text-black font-normal'>
                                                        $84,508.00
                                                    </span>{' '}
                                                    available
                                                </p>
                                            </div>
                                        </td>
                                    </tr>
                                </>
                            }
                        />

                        <div className='mt-4 border-b border-gray2'>
                            <div className='grid grid-cols-2 w-full  border-b border-gray2 mb-4'>
                                <div className='col-span-1 p-4 flex flex-col justify-center'>
                                    <h3>Your loan</h3>
                                    <div className='flex flex-row w-full'>
                                        <p className='text-black text-lg'>
                                            Maximum loan amount: $
                                            <NumberFormat
                                                value={loan?.data.amount ?? 0}
                                                fixedDecimalScale={true}
                                                decimalScale={2}
                                                thousandSeparator={true}
                                                displayType={'text'}
                                                className='inline'
                                            />
                                        </p>
                                    </div>
                                </div>
                                <div className='col-span-1 p-4'>
                                    <p className='text-md'>
                                        Loan term:{' '}
                                        <span className='text-black'>
                                            {loan?.data.term ?? ''} months
                                        </span>
                                    </p>
                                    <p className='text-md'>
                                        Interest rate:{' '}
                                        <span className='text-black'>
                                            {loan?.data.interest_rate * 100 ??
                                                ''}
                                            % APY
                                        </span>
                                    </p>
                                    <p className='text-md'>
                                        Collateral:{' '}
                                        <span className='text-black'>
                                            None required
                                        </span>
                                    </p>
                                </div>
                            </div>
                            <div className='py-8 border-b border-gray2 mb-8'>
                                <p className='text-md'>Purpose of loan*</p>
                                <Input
                                    value={loan?.loan_purpose ?? ''}
                                    onChange={e => {}}
                                    type={'textarea'}
                                    placeholder='Write a description of what the loan will be used for in this space. The is no character limit for this information and it is stored to the backend.'
                                />
                            </div>
                            <div className='grid grid-rows-1 grid-cols-2 gap-8'>
                                <div className='flex flex-col items-center'>
                                    <div className='flex flex-row justify-between font-light w-full mb-4 text-sm '>
                                        <p className='text-gray8'>
                                            Approve loan amount
                                        </p>
                                        <p className='text-gray8'>
                                            Pool balance: $84,608.00 USDC
                                        </p>
                                    </div>
                                    <div className='flex flex-row items-center w-full relative'>
                                        <Input
                                            placeholder='0.00'
                                            value={loanAmount}
                                            onChange={e => {
                                                setLoanAmount(e.target.value)
                                            }}
                                        />
                                        <div className='flex flex-row gap-x-4 absolute right-6'>
                                            <p className=' mb-0'>USDC</p>
                                            <p className='text-black mb-0'>
                                                Max
                                            </p>
                                        </div>
                                    </div>
                                    <div className='flex flex-row justify-between w-full gap-x-4'>
                                        <Button
                                            className='w-1/2 py-4 mt-4'
                                            onClick={e => {
                                                handleApprove()
                                            }}
                                            disabled={!loanAmount}>
                                            Approve
                                        </Button>
                                        <Button
                                            className='w-1/2 py-4 mt-4'
                                            onClick={e => {
                                                handleReject()
                                            }}>
                                            Decline
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>
            </div>
        </>
    )
}
